import React from 'react';
import qs from 'qs';
import { Link } from 'react-router-dom';
import { Button, Tooltip } from 'antd';
import Can from 'config/Can';
import { ROUTES } from 'constants.js';
import OffsetPaginationTable from 'components/Shared/OffsetPaginationTable';
import useTranslation from 'utils/hooks/useTranslation';
import { tabShowsActions } from './helper';

import styles from './style.module.scss';

const STATUS_CLASS = {
  pending: styles.statusPending,
  approved: styles.statusApproved,
  rejected: styles.statusRejected,
};

const FundingRequestsTable = ({
  status,
  dataSource,
  loading,
  currentPage,
  totalCount,
  onReview,
  onPageChange,
  onPageSizeChange,
  onTableChange,
  onDownloadProof,
  downloadingProofId,
}) => {
  const { translateText } = useTranslation();

  const columns = [
    {
      title: translateText('corporateAccounts.fundingRequests.table.account'),
      dataIndex: 'account',
      key: 'account',
      render: (account, record) => (
        <div>
          <Link
            to={{
              pathname: ROUTES.CORPORATIONS_ADMIN,
              search: qs.stringify({ search: record.corporateClientId }),
            }}
            className={styles.accountName}
            data-testid={`fundingRequestsAccountLink-${record.id}`}
          >
            {account}
          </Link>
          <div className={styles.accountId}>{record.transferReference}</div>
        </div>
      ),
    },
    {
      title: translateText('corporateAccounts.fundingRequests.table.transactionId'),
      dataIndex: 'transactionId',
      key: 'transactionId',
      className: styles.secondaryCell,
    },
    {
      title: translateText('corporateAccounts.common.date'),
      dataIndex: 'date',
      key: 'date',
      className: styles.secondaryCell,
      sorter: true,
    },
    {
      title: translateText('corporateAccounts.fundingRequests.table.amount'),
      dataIndex: 'amount',
      key: 'amount',
      sorter: true,
    },
    {
      title: translateText('corporateAccounts.fundingRequests.table.proofOfPayment'),
      dataIndex: 'proofOfPayment',
      key: 'proofOfPayment',
      render: (proofOfPayment, record) => (
        <button
          type="button"
          className={styles.proofLink}
          disabled={downloadingProofId === record.id}
          onClick={() => onDownloadProof({ wireTransferId: record.id, fileName: proofOfPayment })}
          data-testid={`fundingRequestsProofLink-${record.id}`}
        >
          {proofOfPayment}
        </button>
      ),
    },
    {
      title: translateText('corporateAccounts.common.status'),
      dataIndex: 'status',
      key: 'status',
      render: value => (
        <span className={`${styles.statusPill} ${STATUS_CLASS[value] || styles.statusDefault}`}>
          {translateText(`corporateAccounts.fundingRequests.status.${value}`)}
        </span>
      ),
    },
  ];

  if (tabShowsActions(status)) {
    columns.push({
      title: translateText('corporateAccounts.actions'),
      key: 'actions',
      render: (_, record) =>
        record.status === 'pending' ? (
          <Can I="approve" a="FundingRequest" passThrough>
            {allowed => (
              <Tooltip
                title={
                  allowed
                    ? null
                    : translateText('corporateAccounts.fundingRequests.table.reviewDisabled')
                }
              >
                <span>
                  <Button
                    className={styles.reviewButton}
                    disabled={!allowed}
                    onClick={() => onReview(record)}
                    data-testid={`fundingRequestsReviewButton-${record.id}`}
                  >
                    {translateText('corporateAccounts.fundingRequests.table.review')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Can>
        ) : null,
    });
  }

  return (
    <div data-testid="fundingRequestsTable">
      <OffsetPaginationTable
        rowKey="id"
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        current={currentPage}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onChange={onTableChange}
        scroll={{ x: true }}
      />
    </div>
  );
};

export default FundingRequestsTable;
